import { collection, doc, addDoc, deleteDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseDDH';
import { normalizeEmail } from './clients';
import { splitTotal, scheduleFor, applyPercent } from '../constants/pricing';
import { descriptionBullets } from './trackingLinks';
import { QUOTE_VALIDITY_DAYS } from '../constants/business';

const COLLECTION = 'quotes';

// Amounts are plain pounds, so summing them can leave float dust
// (0.1 + 0.2 = 0.30000000000000004). Rounding to pennies keeps the stored
// total equal to what the admin saw when they hit send.
export function lineItemsTotal(lineItems) {
  const total = (lineItems || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  return Math.round(total * 100) / 100;
}

export function formatMoney(amount) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
}

export function formatMonthly(amount) {
  return `${formatMoney(amount)}/mo`;
}

// Derived from the timestamp rather than stored as a status: a stored
// 'expired' value would need something running on a schedule to set it, and
// would be wrong in the meantime.
export function isExpired(quote) {
  if (!quote?.expiresAt || quote.status !== 'sent') return false;
  return quote.expiresAt.toDate() < new Date();
}

export function daysUntilExpiry(quote) {
  if (!quote?.expiresAt) return null;
  const ms = quote.expiresAt.toDate() - new Date();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

// Which monthly rate applies, given how many care invoices have already been
// raised against this quote. Used by both the billing side and the wording, so
// what the client was promised and what they're charged can't drift apart.
export function carePlanRate(carePlan, invoicesRaised = 0) {
  if (!carePlan) return 0;
  const { monthlyAmount, listAmount, discountMonths } = carePlan;
  if (!discountMonths || monthlyAmount >= listAmount) return monthlyAmount;
  return invoicesRaised < discountMonths ? monthlyAmount : listAmount;
}

export function carePlanIsDiscounted(carePlan) {
  return !!carePlan && carePlan.listAmount > carePlan.monthlyAmount;
}

export function formatQuoteDate(ts) {
  return ts ? ts.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
}

// Status values stay lowercase in Firestore because firestore.rules matches
// them exactly — capitalise only for display.
export function formatStatus(status) {
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
}

export const STATUS_LABELS = {
  sent: 'Awaiting your response',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'No longer available',
};

// Withdrawing is deliberately a manual choice, not something inferred from
// another quote being accepted: only we know whether a second quote replaces
// the first or sits alongside it as separate work.
export async function withdrawQuote(id) {
  await updateDoc(doc(db, COLLECTION, id), { status: 'withdrawn' });
}

// Created as 'sent' — there's no draft stage yet, so writing a quote is the
// same action as sending it. `contractUrl` is an externally hosted document
// (Drive, Dropbox) rather than an upload: no Firebase Storage, no billing
// change. Swapping to real uploads later only changes what fills this field.
export async function createQuote(
  {
    clientEmail, clientName, title, category, lineItems, notes, contractUrl, contractText,
    schedule, carePlan, validityDays, discountPercent,
  },
  createdByEmail,
) {
  const items = (lineItems || []).map((item) => ({
    description: item.description.trim(),
    amount: Number(item.amount),
    // What the line covers, as bullets. Priced at the parent, so these carry
    // no amount of their own — they explain what the money buys.
    features: descriptionBullets(item.features || ''),
  }));
  // The line items are the list price; `total` is what's actually owed. Every
  // downstream figure — the payment stages, and the invoices raised from them —
  // reads `total`, so the discount flows through without touching any of them.
  const subtotal = lineItemsTotal(items);
  const discount = Number(discountPercent) || 0;
  const total = applyPercent(subtotal, discount);
  const stages = schedule?.length ? schedule : scheduleFor(category);
  return addDoc(collection(db, COLLECTION), {
    clientEmail: normalizeEmail(clientEmail),
    // Snapshotted, not looked up on read: if the client record is renamed
    // later, an already-signed quote should still show the name it was
    // addressed to.
    clientName: clientName?.trim() || null,
    title: title.trim(),
    category: category === 'app' ? 'app' : 'website',
    // Computed here rather than with serverTimestamp arithmetic, which
    // Firestore can't do. Only an admin can create a quote, so a client
    // can't influence this clock.
    expiresAt: Timestamp.fromDate(
      new Date(Date.now() + (Number(validityDays) || QUOTE_VALIDITY_DAYS) * 24 * 60 * 60 * 1000),
    ),
    lineItems: items,
    subtotal,
    discountPercent: discount,
    discountAmount: Math.round((subtotal - total) * 100) / 100,
    total,
    // Amounts are worked out and stored now, not derived on read: what the
    // client agreed to pay and when is part of the signed terms, so it has to
    // survive any later change to the default schedules.
    paymentSchedule: splitTotal(total, stages),
    // listAmount is our published rate. When monthlyAmount comes in under it,
    // the client's quote strikes the list price through — no separate discount
    // field needed, since undercutting the rate *is* the discount.
    carePlan: carePlan?.tier
      ? {
        tier: carePlan.tier,
        monthlyAmount: Number(carePlan.monthlyAmount) || 0,
        listAmount: Number(carePlan.listAmount) || 0,
        // How many months the reduced rate lasts before reverting to
        // listAmount. Null means it doesn't revert.
        discountMonths: Number(carePlan.discountMonths) || null,
      }
      : null,
    notes: notes?.trim() || null,
    // Stored on the quote itself, so the wording can't drift after signing the
    // way a linked document can. This copy is what the signed PDF renders.
    contractText: contractText?.trim() || null,
    contractUrl: contractUrl?.trim() || null,
    status: 'sent',
    createdBy: createdByEmail,
    createdAt: serverTimestamp(),
    respondedAt: null,
    signedName: null,
  });
}

// The only write a client is allowed to make. firestore.rules pins this down
// to the two status values and three permitted fields, so a client can't edit
// the price of their own quote — and can't accept a quote that carries a
// contract without leaving a name against it.
export async function respondToQuote(id, status, signedName = null) {
  const update = {
    status,
    respondedAt: serverTimestamp(),
  };
  if (status === 'accepted' && signedName) {
    update.signedName = signedName.trim();
  }
  await updateDoc(doc(db, COLLECTION, id), update);
}

export async function deleteQuote(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}
